


























import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Components
import CustomFields from "@/components/app/global/customFields/customFields.vue";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";

@Component({
  components: {
    "custom-fields": CustomFields
  }
})
export default class ClientCustomFieldsForm extends Mixins(
  HasValidation,
  Context
) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    customFields: CustomFields;
  };

  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: false })
  public isSticky!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Data

  public created() {
    this.$_.forEach(this.$_.get(this.client, "custom_fields", []), fieldObj => {
      this.$set(this.form.custom_fields, fieldObj.field.code, fieldObj.value);
    });
  }

  public customFieldsDataModule = DataModules.CUSTOM_FIELDS_CLIENTS;

  public form = { custom_fields: {} } as any;
  public initialForm = { custom_fields: {} } as any;

  public isProcessing = false;
  public isInitiated = false;
  public customFieldsKey = "customFieldsKey";

  // Methods

  public initiateForm(value) {
    this.$emit("customFieldsCount", value);
    this.initialForm = _.cloneDeep(this.form);
    this.isInitiated = true;
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      await this.$refs.customFields.uploadCustomImages();
      await this.updateCustomFields();

      this.$emit("refreshClient");

      if (!this.isSticky) {
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.custom_fields_updated")
        });
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateCustomFields() {
    if (_.isEmpty(this.customFieldsValues)) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.CLIENTS}/update`, {
      id: this.clientId,
      data: {
        custom_fields: this.customFieldsValues
      }
    });
  }

  public revertToInitialState() {
    this.customFieldsKey = this.$uuid();
    this.form = _.cloneDeep(this.initialForm);
  }

  // Computed

  public get clientId(): IClient["id"] {
    return this.client.id;
  }

  public get clientBrandId(): IBrand["id"] {
    return this.client.brand_id || "";
  }

  public get isStaged() {
    return !!this.client?.staged_import;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.initialForm);
  }

  public get customFieldsValues() {
    const differences: any = this.$_.omitBy(this.form.custom_fields, (v, k) => {
      return this.initialForm.custom_fields[k] === v;
    });

    return this.$_.mapValues(differences, fieldValue =>
      fieldValue === "" ? null : fieldValue
    );
  }

  // Guarding

  public get canUpdate() {
    return this.$userCan("update_client");
  }

  // Watchers

  @Watch("client")
  public onClientDataChange() {
    this.isInitiated = false;
    this.customFieldsKey = this.$uuid();
  }

  @Watch("formIsChanged")
  public onIsEnabledChange() {
    if (!this.isInitiated) return;

    this.$emit("isEnabled", this.formIsChanged);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);

    if (!this.isProcessing && !this.hasErrors) {
      this.$emit("isEnabled", false);
    }
  }
}
